import { Component, OnInit, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { Observable, distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { CmsCollection, CmsContent, CmsImageData, CmsTemplate, CmsViewType } from '@fcom/core-api';
import { isPresent, safeMap, sanitizeWhiteListedPath } from '@fcom/core/utils';
import { ConfigService, isEmptyObjectOrHasEmptyValues } from '@fcom/core';
import { finShare } from '@fcom/rx';
import { BookingService } from '@fcom/booking/services';
import { LocationPair, selectGlobalBookingFlights } from '@fcom/common/store';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { AspectRatios, FetchPriorityEnum } from '@fcom/ui-components';
import { FinnairThemeService } from '@fcom/common/services';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { CmsTemplateService } from '../../services';
import { findByType } from '../../utils';

/**
 * Booking entry component for Front page. If you need just a BookingWidget then use it directly.
 * This component contains BookingWidget, Disruptions bar and hero offer.
 */
@Component({
  selector: 'fin-booking-entry',
  styleUrls: ['./booking-entry.component.scss'],
  templateUrl: './booking-entry.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BookingEntryComponent implements OnInit {
  readonly FetchPriorityEnum = FetchPriorityEnum;
  private bookingService = inject(BookingService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private cmsTemplateService = inject(CmsTemplateService);
  private configService = inject(ConfigService);
  private mediaQueryService = inject(MediaQueryService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private themeService = inject(FinnairThemeService);

  defaultLocations: LocationPair[] = [];

  readonly isNewBrand = computed(() => {
    return this.themeService.siteTheme() !== FinnairTheme.LEGACY;
  });

  private readonly sanitizedPath$: Observable<string> = this.route.url.pipe(
    map(() => this.route.pathFromRoot.map((r) => r.snapshot.url.join('/')).filter(Boolean)),
    map((fragments) => sanitizeWhiteListedPath(`/${fragments.join('/')}`, this.configService.cfg)),
    distinctUntilChanged()
  );

  private readonly contents$: Observable<CmsContent[]> = this.sanitizedPath$.pipe(
    switchMap((path) => this.cmsTemplateService.load(path)),
    map((template: CmsTemplate) => safeMap(template, (t: CmsTemplate) => t.main)),
    filter(isPresent),
    finShare()
  );

  readonly disruptions$: Observable<CmsCollection> = this.contents$.pipe(
    findByType(CmsViewType.FRONT_PAGE_DISRUPTIONS, false)
  );

  private readonly isMobile = this.mediaQueryService.isBreakPoint('mobile');

  readonly aspectRatio = computed<keyof typeof AspectRatios>(() => {
    return this.isMobile() ? '1x1' : '16x9';
  });

  readonly campaignImg = toSignal(
    this.contents$.pipe(
      findByType(CmsViewType.FRONT_PAGE_OFFER, false),
      map((campaign: CmsContent) => campaign?.picture)
    )
  );

  readonly globalBookingFlights = this.store$.selectSignal(selectGlobalBookingFlights);

  readonly destinationImg = computed(() => {
    return this.globalBookingFlights()[0]?.destination?.picture;
  });

  readonly isCampaignImg = computed(() => {
    return !this.destinationImg();
  });

  readonly backgroundImg = computed<CmsImageData>(() => {
    return this.destinationImg() ?? this.campaignImg();
  });

  ngOnInit(): void {
    this.updateDataFromQueryParams();
  }

  private updateDataFromQueryParams(): void {
    const queryParams: Params = this.route.snapshot.queryParams;

    if (isEmptyObjectOrHasEmptyValues(queryParams)) {
      return;
    }

    if (isPresent(queryParams.tripType)) {
      this.bookingService.updateTripTypeBasedOnQueryParams(queryParams);
    }

    if (isPresent(queryParams.origin) || isPresent(queryParams.destination)) {
      this.defaultLocations = [
        {
          ...(isPresent(queryParams.origin) && { origin: { locationCode: queryParams.origin } }),
          ...(isPresent(queryParams.destination) && { destination: { locationCode: queryParams.destination } }),
        },
      ] as LocationPair[];
    }

    // After updating we want to clear the queryParams so we do an "in-place" navigation and replace the url with empty
    this.router.navigate([], {
      queryParams: {},
      replaceUrl: true,
    });
  }
}
